import { useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import Rating from 'react-rating-stars-component';
import ChallengeReviews from '../ChallengeReview';
import { updateChallenge } from '../../services/ChallengesService';
import { pick } from '../../utils/objectUtils'; 
import CreatableSelect from 'react-select/creatable';
import {
  CHALLENGE_CATEGORIES,
  CHALLENGE_TYPES,
  CHALLENGE_LEVELS,
  TOURNAMENT_LEVELS
} from '../../utils/constants';
import './styles.css';
import { useNavigate, useLocation } from 'react-router-dom';

pdfjs.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.12.313/pdf.worker.min.js';

export default function ChallengeDetails({
    challenge, 
    reviews, 
    allTags, 
    onChallengeUpdated 
}) {
  const [pageNumber, setPageNumber] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const [scale, setScale] = useState(1.2);

  const [isEditing, setIsEditing] = useState(false);
  const [editedChallenge, setEditedChallenge] = useState({ ...challenge });
  const [selectedTags, setSelectedTags] = useState(
    Array.isArray(challenge.tags)
      ? challenge.tags.map(tag => ({ label: tag, value: tag }))
      : []
  );

  // Back button functionality
  const location = useLocation();
  const navigate = useNavigate();
  
  const handleBack = () => {
    navigate(`/challenges${location.search}`);
  };

  const avgRating = reviews.length
    ? reviews.reduce((sum, r) => sum + r.overall_rating, 0) / reviews.length
    : null;


    const handleSave = async () => {
        const payload = {
            ...pick(editedChallenge, [
                'name',
                'type',
                'category',
                'tournament_level',
                'level',
                'source_id',
            ]),
            tags: selectedTags.map(tag => tag.value),
        };
        // Convert empty strings to null
        for (const key in payload) {
            if (payload[key] === '') {
            payload[key] = null;
            }
        }
        try {
            const updated = await updateChallenge(challenge.id, payload);
            onChallengeUpdated(updated);

            // Replace challenge data with updated result
            setEditedChallenge(updated);
            setIsEditing(false);
        } catch (err) {
            console.error('Error saving challenge:', err);
            alert('Failed to save changes. Please try again.');
        }
    };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedChallenge({ ...challenge });
    setSelectedTags(challenge.tags.map(tag => ({ label: tag, value: tag })));
  };

  const handleDocumentLoad = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  return (
    <div className="challenge-detail-container">
      {/* PDF Sidebar */}
      <div className="pdf-sidebar">
        <div className="pdf-controls">
          <button onClick={() => setScale(scale - 0.2)} disabled={scale <= 0.6}>–</button>
          <span>Zoom: {(scale * 100).toFixed(0)}%</span>
          <button onClick={() => setScale(scale + 0.2)} disabled={scale >= 3}>+</button>
        </div>
        <div className="pdf-pagination">
          <button onClick={() => setPageNumber(Math.max(pageNumber - 1, 1))}>Previous</button>
          <span>Page {pageNumber} of {numPages || '...'}</span>
          <button onClick={() => setPageNumber(Math.min(pageNumber + 1, numPages))}>Next</button>
        </div>
        <Document
          file={`/pdfs/${encodeURIComponent(challenge.name)}.pdf`}
          onLoadSuccess={handleDocumentLoad}
        >
          <Page pageNumber={pageNumber} scale={scale} />
        </Document>
      </div>

      {/* Challenge Info */}
      <div className="details-panel">
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            
          <h1>{challenge.name}</h1>
          <div className="details-panel">
            <button onClick={handleBack} className="c-btn">← Back to Library</button>
            {/* ...rest of ChallengeDetail */}
          </div>
          <div>
            {!isEditing ? (
              <button onClick={() => setIsEditing(true)} className="c-btn">Edit</button>
            ) : (
              <>
                <button onClick={handleSave} className="c-btn c-btn--primary">Save</button>
                <button onClick={handleCancel} className="c-btn c-btn--danger" style={{ marginLeft: '10px' }}>Cancel</button>
              </>
            )}
          </div>
        </div>

        <div className="rating-block">
          <Rating value={avgRating || 0} edit={false} size={20} isHalf={true} />
          <span style={{ marginLeft: 8 }}>
            {avgRating ? `${avgRating.toFixed(1)} / 5` : 'No reviews yet'}
          </span>
        </div>

        <hr />

        {/* Editable fields */}
        <div className="a-section">
            
          <div className="a-color-base a-size-base"><strong>Challenge Level:</strong> 
            {isEditing ? (
              <select
                value={editedChallenge.level || ''}
                onChange={(e) => setEditedChallenge({ ...editedChallenge, level: e.target.value })}
              >
                <option value="">Select Level</option>
                {CHALLENGE_LEVELS.map((lvl) => (
                  <option key={lvl} value={lvl}>{lvl}</option>
                ))}
              </select>
            ) : (
              challenge.level
            )}
          </div>

          <div className="a-color-base a-size-base"><strong>Tournament Level:</strong> 
          {isEditing ? (
              <select
                value={editedChallenge.tournament_level || ''}
                onChange={(e) => setEditedChallenge({ ...editedChallenge, tournament_level: e.target.value })}
              >
                <option value="">Select Tournament Level</option>
                {TOURNAMENT_LEVELS.map((t_lvl) => (
                  <option key={t_lvl} value={t_lvl}>{t_lvl}</option>
                ))}
              </select>
            ) : (
              challenge.tournament_level
            )}
          </div>

          <div className="a-color-base a-size-base"><strong>Type:</strong> 
            {isEditing ? (
              <select
                value={editedChallenge.type || ''}
                onChange={(e) => setEditedChallenge({ ...editedChallenge, type: e.target.value })}
              >
                <option value="">Select Type</option>
                {CHALLENGE_TYPES.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            ) : (
              challenge.type
            )}
          </div>

          <div className="a-color-base a-size-base"><strong>Category:</strong> 
            {isEditing ? (
              <select
                value={editedChallenge.category || ''}
                onChange={(e) => setEditedChallenge({ ...editedChallenge, category: e.target.value })}
              >
                <option value="">Select Category</option>
                {CHALLENGE_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            ) : (
              challenge.category
            )}
          </div>
            <div className="a-color-base a-size-base">
                <strong>PDFs:</strong> {challenge.category}
                <a
                className="challenge-link"
                href={
                    challenge.source_url && challenge.source_url.startsWith('http')
                    ? challenge.source_url
                    : `/pdfs/${encodeURIComponent(challenge.name)}.pdf`
                }
                target="_blank"
                rel="noreferrer"
                >
                    View PDF
                </a>
            </div>
            <div className="a-color-base a-size-base">
                <strong>Origin:</strong>{' '}
                <a
                className="challenge-link"
                href={
                    challenge.source_url && challenge.source_url.startsWith('http')
                    ? challenge.source_url
                    : `/source_pdfs/${encodeURIComponent(challenge.source_url)}`
                }
                target="_blank"
                rel="noreferrer"
                >
                View Source Collection
                </a>
            </div>
          </div>
        <hr />

        {/* TAGS / KEYWORDS */}
        <div className="a-section">
          <p className="bold-weight-text">Keywords:</p>
          {isEditing ? (
            <CreatableSelect
              isMulti
              value={selectedTags}
              options={(Array.isArray(allTags) ? allTags : []).map(tag => ({ label: tag, value: tag }))}
              onChange={(newTags) => setSelectedTags(newTags)}
              placeholder="Add or create tags..."
            />
          ) : (
            <div className="tag-grid">
              {Array.isArray(challenge.tags) && challenge.tags.length > 0 ? (
                challenge.tags.map(tag => (
                  <span className="tag-pill" key={tag}>{tag}</span>
                ))
              ) : (
                <span>No tags</span>
              )}
            </div>
          )}
        </div>

        <hr />

        <ChallengeReviews challenge={challenge} reviews={reviews} />
      </div>
    </div>
  );
}
